import { useWalletNetwork } from "../../networks/WalletNetworkContext";
import { useCallback, useEffect, useRef, useState } from "react";
import nacl from "tweetnacl";
import { ethers } from "ethers";
import type { SolanaNetworkConfig } from "../../config/solana";
import { disposeFlatJoinSplitV2Prover } from "../../joinsplit-v2/prover";
import {
  connectSolanaWallet,
  disconnectSolanaWallet,
  onSolanaWalletChange,
  signSolanaMessage,
  subscribeSolanaWallets,
  type ConnectedSolanaWallet,
  type WalletStandardWallet,
} from "../../joinsplit-v2/solana/walletStandard";
import {
  buildLegacySolanaDerivationMessage,
  buildSolanaDerivationMessage,
} from "../../joinsplit-v2/solana/derivation";
import { deriveSpendingKeys } from "../../joinsplit/keys";
import {
  carryOverCommit,
  hasSavedWalletData,
  hasStoredCarryOverWallet,
  resolveCarryOver,
  type CarryOverDecision,
} from "../../joinsplit-v2/walletCarryOver";
import {
  hasRecoveryKeyOverride,
  hasStoredRecoveryWallet,
  persistRecoveryWallet,
  recoveryPhraseToWalletMaterial,
  resolveRecoveryWallet,
  restoreRecoveryPhrase,
} from "../../joinsplit-v2/recoveryPhrase";
import type { SolanaLogType, SolanaWalletState } from "../types";
import {
  clearMasterRecoverySession,
  hasStoredMasterRecoverySession,
} from "../../joinsplit-v2/masterRecoveryStorage";
import {
  connectMasterRecoveryWallet,
  type MasterRecoveryBundleLoader,
} from "../../joinsplit-v2/masterRecoveryConnection";

const DISCONNECTED: SolanaWalletState = {
  connected: false,
  address: "",
  connection: null,
  spendingKeys: null,
  walletName: null,
};

/** Wallet Standard wallets installed in this browser, updated as they announce. */
export function useSolanaWallets(): WalletStandardWallet[] {
  const [wallets, setWallets] = useState<WalletStandardWallet[]>([]);
  useEffect(() => subscribeSolanaWallets(setWallets), []);
  return wallets;
}

export function shortSolanaAddress(address: string): string {
  return address.length > 12
    ? `${address.slice(0, 4)}…${address.slice(-4)}`
    : address;
}

export function useSolanaWallet({
  network,
  addLog,
  loadMasterRecoveryBundle,
}: {
  network: SolanaNetworkConfig;
  addLog: (message: string, type?: SolanaLogType) => void;
  loadMasterRecoveryBundle?: MasterRecoveryBundleLoader;
}) {
  const acquireNetworkLock = useWalletNetwork()?.acquireLock;
  const [wallet, setWallet] = useState<SolanaWalletState>(DISCONNECTED);
  // Keep the verified setup signature for legacy handles and carry-over.
  // Proxy authentication uses the active private wallet's spending keys.
  const derivationSignatureRef = useRef<Uint8Array | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const connectionRef = useRef<ConnectedSolanaWallet | null>(null);
  const lastCandidateRef = useRef<WalletStandardWallet | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const networkIdentity = `${network.key}:${network.cluster}:${network.programId}:${network.enabled}`;
  const networkIdentityRef = useRef(networkIdentity);
  networkIdentityRef.current = networkIdentity;

  const clearPrivateSession = useCallback(() => {
    generationRef.current++;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    derivationSignatureRef.current = null;
    connectionRef.current = null;
    disposeFlatJoinSplitV2Prover();
    if (mountedRef.current) setWallet(DISCONNECTED);
  }, []);

  const disconnectWallet = useCallback(async () => {
    const connection = connectionRef.current;
    clearMasterRecoverySession();
    lastCandidateRef.current = null;
    clearPrivateSession();
    if (connection) await disconnectSolanaWallet(connection);
  }, [clearPrivateSession]);

  const connectWallet = useCallback(
    async (
      candidate: WalletStandardWallet,
      {
        existingWallet = false,
        recoveryPhrase,
        legacyRecoveryPhrase = false,
        linkMasterWallet = false,
        createSeparateMasterWallet = false,
      }: {
        /** The user had a private wallet before the signing message changed. */
        existingWallet?: boolean;
        /** A phrase exported by StealthOS for this private chain wallet. */
        recoveryPhrase?: string;
        legacyRecoveryPhrase?: boolean;
        linkMasterWallet?: boolean;
        /** Explicitly create a new vault without touching other public wallets. */
        createSeparateMasterWallet?: boolean;
      } = {},
    ) => {
      if (!network.enabled) {
        throw new Error(
          network.configurationError
            ? `${network.label} is misconfigured: ${network.configurationError}`
            : `${network.label} is not configured in this deployment`,
        );
      }
      // Malformed phrases fail before requesting a wallet signature. A valid
      // public-wallet phrase cannot be distinguished by format; the UI warns
      // that only a phrase exported by StealthOS belongs here.
      const restoring = recoveryPhrase !== undefined;
      if (
        createSeparateMasterWallet &&
        (!network.onchainRecoveryEnabled || restoring || linkMasterWallet)
      )
        throw new Error(
          "Choose either an independent private wallet or a recovery/link action.",
        );
      const phraseMaterial = restoring
        ? recoveryPhraseToWalletMaterial(recoveryPhrase)
        : null;
      clearPrivateSession();
      const generation = generationRef.current;
      const isCurrent = () =>
        mountedRef.current &&
        generationRef.current === generation &&
        networkIdentityRef.current === networkIdentity;
      const requireCurrent = () => {
        if (!isCurrent())
          throw new Error(
            "Your wallet connection changed during setup. Reconnect and retry.",
          );
      };
      const releaseNetwork = acquireNetworkLock?.();
      let unsubscribeAttempt: (() => void) | null = null;
      let connected = false;
      try {
        addLog(`Connecting via ${candidate.name}...`, "info");
        const connection = await connectSolanaWallet(
          candidate,
          `solana:${network.cluster}`,
        );
        requireCurrent();
        connectionRef.current = connection;
        // Listen before signing/deriving so an account switch cannot save a
        // recovery vault or activate a session for the previous account.
        unsubscribeAttempt = onSolanaWalletChange(connection, (accounts) => {
          if (
            isCurrent() &&
            accounts &&
            !accounts.some(
              (account) =>
                account.address === connection.address &&
                account.publicKey.length === connection.publicKey.length &&
                account.publicKey.every(
                  (byte, index) => byte === connection.publicKey[index],
                ) &&
                (account.chains.length === 0 ||
                  account.chains.includes(connection.chain)),
            )
          ) {
            addLog(
              "Wallet account changed; disconnected the private session",
              "warning",
            );
            clearPrivateSession();
          }
        });
        unsubscribeRef.current = unsubscribeAttempt;
        requireCurrent();
        addLog(
          `Connected ${shortSolanaAddress(connection.address)}; creating your private wallet...`,
          "info",
        );
        const params = {
          wallet: connection.address,
          networkKey: network.key,
          programId: network.programId,
          origin: window.location.origin,
        };
        const carryOver = {
          scope: `solana:${network.key}:${network.programId}`,
          wallet: connection.address,
        };
        const savedDataSuffix = `:${network.key}:${network.programId}:${connection.address}`;
        const hasPreviousWalletData = () =>
          hasSavedWalletData((key) => key.includes(savedDataSuffix));
        const legacyPhrase =
          restoring &&
          (legacyRecoveryPhrase || !phraseMaterial?.recoveryEntropy);
        const useMasterWallet =
          !!network.onchainRecoveryEnabled && !legacyPhrase;
        const hasLegacyWallet =
          !useMasterWallet &&
          !restoring &&
          (existingWallet ||
            hasStoredRecoveryWallet(carryOver) ||
            hasStoredCarryOverWallet(carryOver) ||
            hasPreviousWalletData());
        const masterTarget = {
          scope: `master-v1:solana:${network.cluster}:${network.programId}`,
          wallet: connection.address,
        };
        const hasMasterWallet =
          !!network.onchainRecoveryEnabled &&
          hasStoredMasterRecoverySession(masterTarget);
        const create12WordWallet =
          !useMasterWallet && !restoring && !hasLegacyWallet;
        const recoveryOverride =
          restoring ||
          useMasterWallet ||
          hasMasterWallet ||
          hasRecoveryKeyOverride(carryOver);
        const verifySignature = (
          message: Uint8Array,
          signature: Uint8Array,
        ) => {
          let verified = false;
          try {
            verified = nacl.sign.detached.verify(
              message,
              signature,
              connection.publicKey,
            );
          } catch {}
          if (!verified)
            throw new Error(
              "The wallet did not sign the requested message with the connected Solana account. Reconnect and retry.",
            );
        };
        const signVerified = async (messageText: string) => {
          requireCurrent();
          const message = new TextEncoder().encode(messageText);
          // Preserve the exact requested bytes even if a wallet mutates its input.
          const signature = await signSolanaMessage(
            connection,
            new Uint8Array(message),
          );
          requireCurrent();
          verifySignature(message, signature);
          return signature;
        };
        const newSignature = await signVerified(
          buildSolanaDerivationMessage(params),
        );
        // A restored key takes precedence over old signature carry-over. Keep
        // old carry-over records intact for existing handles and older clients.
        let decision: CarryOverDecision | null = null;
        let signature = newSignature;
        if (!recoveryOverride) {
          decision = await resolveCarryOver(carryOver, newSignature, {
            existingWallet,
            hasPreviousWalletData,
          });
          requireCurrent();
          if (decision.kind === "carried") {
            signature = decision.signature;
            verifySignature(
              new TextEncoder().encode(
                buildLegacySolanaDerivationMessage(params),
              ),
              signature,
            );
          } else if (decision.kind === "needs-previous") {
            addLog(
              "One more signature brings your existing private wallet to this device. You only do this once here.",
              "info",
            );
            signature = await signVerified(
              buildLegacySolanaDerivationMessage(params),
            );
          }
        }
        const creating12WordWallet =
          create12WordWallet && decision?.kind === "new";
        const legacyMaterial = !useMasterWallet
          ? (phraseMaterial ??
            (await resolveRecoveryWallet(
              carryOver,
              newSignature,
              new Uint8Array(
                await crypto.subtle.digest(
                  "SHA-256",
                  new Uint8Array(signature).buffer,
                ),
              ),
              {
                isCurrent,
                create12WordWallet: creating12WordWallet,
              },
            )))
          : undefined;
        requireCurrent();
        const master = useMasterWallet
          ? await connectMasterRecoveryWallet({
              target: masterTarget,
              signature: newSignature,
              network: {
                chain: "solana",
                chainDomain: ethers.toBeHex(network.chainDomainId, 32),
              },
              phrase: recoveryPhrase,
              link: linkMasterWallet,
              createSeparate: createSeparateMasterWallet,
              isCurrent,
              loadBundle: loadMasterRecoveryBundle,
              confirmReplacement: () =>
                window.confirm(
                  "This phrase opens a different StealthOS wallet. Save the current recovery phrase before switching. Existing encrypted backups will be kept. Switch wallets?",
                ),
            })
          : null;
        requireCurrent();
        const material = master?.material ?? legacyMaterial!;
        const spendingKeys = {
          ...(await deriveSpendingKeys(material.spendingKey)),
          recoveryEntropy: material.recoveryEntropy,
          ...(master ? { masterRecovery: master.masterRecovery } : {}),
        };
        requireCurrent();
        if (!master && restoring) {
          await restoreRecoveryPhrase(carryOver, newSignature, recoveryPhrase, {
            isCurrent,
            allowCorruptRecordReplacement: true,
            confirmIdentityReplacement: () =>
              window.confirm(
                "This phrase opens a different private wallet. Save the current wallet's StealthOS recovery phrase first if you still need it. Switch private wallets? Existing encrypted note backups will be kept.",
              ),
          });
        } else if (!master) {
          if (creating12WordWallet)
            await persistRecoveryWallet(carryOver, newSignature, material, {
              isCurrent,
            });
          requireCurrent();
          if (decision) {
            await carryOverCommit(
              carryOver,
              decision,
              newSignature,
              signature,
              {
                isCurrent,
              },
            )();
          }
          requireCurrent();
          await persistRecoveryWallet(carryOver, newSignature, material, {
            isCurrent,
          });
        }
        requireCurrent();
        derivationSignatureRef.current = signature;
        lastCandidateRef.current = candidate;
        setWallet({
          connected: true,
          address: connection.address,
          connection,
          spendingKeys,
          walletName: candidate.name,
        });
        connected = true;
        addLog("Private wallet ready", "success");
      } finally {
        if (!connected) {
          unsubscribeAttempt?.();
          if (isCurrent()) clearPrivateSession();
        }
        releaseNetwork?.();
      }
    },
    [
      acquireNetworkLock,
      addLog,
      network,
      networkIdentity,
      clearPrivateSession,
      loadMasterRecoveryBundle,
    ],
  );

  const linkMasterWallet = useCallback(async () => {
    if (!lastCandidateRef.current)
      throw new Error(
        "Connect the chain wallet before linking its recovery phrase.",
      );
    await connectWallet(lastCandidateRef.current, {
      linkMasterWallet: true,
      existingWallet: true,
    });
  }, [connectWallet]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPrivateSession();
    };
  }, [clearPrivateSession]);
  useEffect(
    () => clearPrivateSession(),
    [networkIdentity, clearPrivateSession],
  );

  return {
    wallet,
    connectWallet,
    disconnectWallet,
    derivationSignatureRef,
    linkMasterWallet,
  };
}
